import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/themes.css";
import "@/super-tech.css";
import App from "@/App";
import ErrorBoundary from "@/components/ErrorBoundary";

// Auto-recover from stale-build errors (deploy replaced hashed files) + crash reporting
import { reportClientError, reloadOnceForStaleBuild, isStaleBuildError } from "@/utils/errorReporter";
window.addEventListener("error", (e) => {
  reportClientError({ message: e.message, stack: e.error?.stack, source: "window-error" });
  reloadOnceForStaleBuild(e.message);
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = e.reason?.message || e.reason;
  if (isStaleBuildError(msg) || e.reason instanceof Error) {
    reportClientError({ message: msg, stack: e.reason?.stack, source: "unhandledrejection" });
  }
  reloadOnceForStaleBuild(msg);
});

// 🔐 Desktop app: require login on every launch (closing the app = logged out).
// sessionStorage survives in-app reloads but not an app restart. Price-checker
// kiosk sessions are exempt so the price screen keeps working unattended.
try {
  const isDesktop = !!window.selloxDesktop || navigator.userAgent.includes("Electron");
  if (isDesktop && !sessionStorage.getItem("sx_desktop_boot")) {
    sessionStorage.setItem("sx_desktop_boot", "1");
    let keep = false;
    try { keep = JSON.parse(localStorage.getItem("cached_auth_user") || "null")?.role === "price_checker"; } catch {}
    if (!keep) {
      ["token", "cached_auth_user", "cached_auth_permissions"].forEach(k => localStorage.removeItem(k));
    }
  }
} catch {}

// Desktop (Windows): native confirm/alert/prompt leave the page unable to receive clicks or
// typing until the window is blurred+refocused → ask main process to do that after each dialog.
try {
  if (window.selloxDesktop?.focusFix) {
    ["confirm", "alert", "prompt"].forEach(fn => {
      const orig = window[fn].bind(window);
      window[fn] = (...a) => { try { return orig(...a); } finally { setTimeout(() => window.selloxDesktop.focusFix(), 0); } };
    });
  }
  // Electron has no window.prompt() (returns null silently) → sync IPC prompt rendered by the main process (2026-10-24: 桌面改客户密码没反应)
  if (window.selloxDesktop?.prompt) {
    window.prompt = (msg, def) => {
      try { return window.selloxDesktop.prompt(String(msg ?? ""), def == null ? "" : String(def)); }
      finally { setTimeout(() => window.selloxDesktop.focusFix?.(), 0); }
    };
  }
} catch {}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ErrorBoundary label="AppRoot" onReset={() => window.location.reload()}>
    <App />
  </ErrorBoundary>,
);

// Register Service Worker for offline support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(reg => {
        console.log('SW registered:', reg.scope);
        // Check for updates every hour
        setInterval(() => reg.update(), 60 * 60 * 1000);

        // Wait for SW to be active, then precache all page assets
        if (navigator.serviceWorker.controller) {
          precacheAssets();
        }
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          precacheAssets();
        });

        // Also precache after SW update is found and installed
        reg.addEventListener('updatefound', () => {
          const newSW = reg.installing;
          if (newSW) {
            newSW.addEventListener('statechange', () => {
              if (newSW.state === 'activated') {
                precacheAssets();
              }
            });
          }
        });
      })
      .catch(err => console.warn('SW registration failed:', err));
  });
}

function precacheAssets() {
  // Collect ALL static assets from the current page
  const assets = new Set();
  document.querySelectorAll('script[src]').forEach(el => {
    if (el.src) assets.add(el.src);
  });
  document.querySelectorAll('link[rel="stylesheet"][href]').forEach(el => {
    if (el.href) assets.add(el.href);
  });
  document.querySelectorAll('link[rel="preload"][href], link[rel="prefetch"][href]').forEach(el => {
    if (el.href) assets.add(el.href);
  });
  // Also grab any dynamically loaded chunks visible in performance entries
  if (performance && performance.getEntriesByType) {
    performance.getEntriesByType('resource').forEach(entry => {
      if (entry.name && (entry.name.includes('/static/') || entry.name.match(/\.(js|css)$/))) {
        assets.add(entry.name);
      }
    });
  }
  const urls = Array.from(assets).filter(u => u.startsWith(location.origin));
  if (urls.length > 0 && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'PRECACHE_ASSETS', urls });
    console.log(`SW: requested precache of ${urls.length} assets`);
  }
}
